<template>
    <div
        ref="container"
        class="picker-swap"
        :style="containerStyle"
        :class="{ 'pointer-events-none opacity-50': disabled, 'picker-swap--resizing': isContainerHeightPinned }"
    >
        <transition
            name="fade-transition"
            mode="out-in"
            @before-leave="pinContainerToCurrentHeight"
            @enter="animateContainerToIncomingViewHeight"
            @after-enter="finishViewSwap"
            @after-leave="notifyHostFormClosed"
        >
            <!-- Colors view -->
            <div v-if="!showForm" key="colors" class="flex flex-wrap items-center gap-2 overflow-visible">
                <!-- Built-in colors (circles) -->
                <button
                    v-for="color in builtInColors"
                    :key="'default-' + color.slug"
                    v-tooltip="localizations[lang][color.label] || color.label"
                    type="button"
                    :aria-label="localizations[lang][color.label] || color.label"
                    :aria-pressed="String(selectedSlug === color.slug)"
                    :disabled="disabled"
                    class="block h-5 w-5 cursor-pointer rounded-full border-none transition-opacity duration-150 tap-target hover:opacity-80 focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    :class="builtInActiveClasses(color)"
                    :style="{ backgroundColor: color.color }"
                    @click.stop="$emit('select', { slug: color.slug, color: color.color })"
                ></button>

                <!-- Custom colors (rounded squares) -->
                <HighlighterCustomColor
                    v-for="color in customColors"
                    :key="'custom-' + color.slug"
                    :color="color"
                    :selected-slug="selectedSlug"
                    :variant="variant"
                    :disabled="disabled"
                    @select="$emit('select', $event)"
                    @edit="openHighlighterForm"
                />

                <!-- Add button -->
                <button
                    v-if="canAddMoreHighlighters"
                    type="button"
                    data-role="add-highlighter"
                    class="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-dashed border-theme-toolbar-icon bg-transparent text-theme-toolbar-icon opacity-60 transition-opacity tap-target hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    :title="localizations[lang].newHighlighter"
                    :aria-label="localizations[lang].newHighlighter"
                    :disabled="disabled"
                    @click="openHighlighterForm()"
                >
                    <SvgIcon icon="plus-bold" width="10" height="10" />
                </button>

                <!-- Delete highlight button -->
                <button
                    v-if="showDeleteHighlight"
                    v-tooltip="localizations[lang].tooltips.deleteHighlight"
                    type="button"
                    :aria-label="localizations[lang].tooltips.deleteHighlight"
                    :disabled="disabled"
                    class="flex h-5 w-5 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-theme-toolbar-icon tap-target focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    @click.stop="$emit('delete-highlight')"
                >
                    <SvgIcon icon="water-drop" width="16" height="16" />
                </button>
            </div>

            <!-- Add/edit form -->
            <HighlighterAddForm
                v-else
                key="form"
                :variant="variant"
                :edited-highlighter="editedHighlighter"
                @added="handleColorAdded"
                @updated="closeHighlighterForm"
                @cancel="closeHighlighterForm"
            />
        </transition>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import HighlighterCustomColor from './HighlighterCustomColor.vue';
import HighlighterAddForm from './HighlighterAddForm.vue';

export default {
    name: 'HighlighterColorPicker',

    components: {
        HighlighterCustomColor,
        HighlighterAddForm,
    },

    props: {
        /**
         * Layout variant: 'compact' for dropdown, 'standard' for modal.
         */
        variant: {
            type: String,
            default: 'standard',
            validator: value => ['compact', 'standard'].includes(value),
        },
        /**
         * Currently selected/active color slug for visual indicator.
         */
        selectedSlug: {
            type: String,
            default: '',
        },
        /**
         * Whether to show the "delete highlight" water-drop button.
         */
        showDeleteHighlight: {
            type: Boolean,
            default: false,
        },
        /**
         * Disables every color action and dims the palette.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['select', 'delete-highlight', 'form-toggle'],

    data() {
        return {
            showForm: false,
            /** The custom color the form is editing, or null when it is creating one. */
            editedHighlighter: null,
            /** Height the container is pinned to while a swap is in flight. */
            pinnedContainerHeight: null,
        };
    },

    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['allHighlighters', 'canAddMoreHighlighters']),

        builtInColors() {
            return this.allHighlighters.filter(c => !c.custom);
        },

        customColors() {
            return this.allHighlighters.filter(c => c.custom);
        },

        isContainerHeightPinned() {
            return this.pinnedContainerHeight !== null;
        },

        containerStyle() {
            return this.isContainerHeightPinned ? { height: `${this.pinnedContainerHeight}px` } : {};
        },
    },

    watch: {
        disabled(isDisabled) {
            if (isDisabled) this.closeHighlighterForm();
        },
    },

    methods: {
        /**
         * The host is told before the form arrives.
         * @param {{ slug: string, color: string, label: string }|null} [editedHighlighter] - The color to edit
         */
        openHighlighterForm(editedHighlighter = null) {
            this.editedHighlighter = editedHighlighter;
            this.showForm = true;
            this.$emit('form-toggle', true);
        },

        closeHighlighterForm() {
            this.showForm = false;
        },

        /**
         * Returns active indicator classes for built-in color buttons.
         * @param {{ slug: string }} color - The color to check
         * @returns {Object} Class bindings
         */
        builtInActiveClasses(color) {
            const isActive = this.selectedSlug === color.slug;

            if (this.variant === 'compact') {
                return {
                    'relative transition-transform duration-50 ease-in': isActive,
                    "after:content-[''] after:absolute after:inset-0 after:border after:border-solid after:border-2 after:border-current after:rounded-full after:scale-120":
                        isActive,
                };
            }

            return { 'ring-2 ring-theme-text ring-offset-2': isActive };
        },

        /**
         * Handles a newly added custom color by closing the form and selecting it.
         * @param {{ slug: string, color: string }} payload - The newly created color data
         */
        handleColorAdded(payload) {
            this.closeHighlighterForm();
            this.$emit('select', payload);
        },

        /**
         * Tells the host only once the form has left, so its own controls never overlap
         * a form that is still fading out.
         */
        notifyHostFormClosed() {
            if (!this.showForm) this.$emit('form-toggle', false);
        },

        pinContainerToCurrentHeight() {
            const container = this.$refs.container;
            if (container) this.pinnedContainerHeight = container.offsetHeight;
        },

        /**
         * Moves the pinned height to the incoming view's own height, so the resize
         * animates in both directions instead of jumping.
         * @param {HTMLElement} incomingView - The view the transition is bringing in
         */
        animateContainerToIncomingViewHeight(incomingView) {
            if (!this.isContainerHeightPinned) return;
            const incomingHeight = incomingView.offsetHeight;
            this.pinnedContainerHeight = incomingHeight === this.pinnedContainerHeight ? null : incomingHeight;
        },

        /**
         * Hands the height back to the content once the swap has landed, and returns
         * focus to the control the form was opened from when it closed.
         */
        finishViewSwap() {
            this.pinnedContainerHeight = null;
            if (!this.showForm) this.focusFormOriginControl();
        },

        focusFormOriginControl() {
            const originSelector = this.editedHighlighter
                ? `[data-color-slug="${this.editedHighlighter.slug}"]`
                : '[data-role="add-highlighter"]';
            this.$refs.container?.querySelector(originSelector)?.focus();
        },
    },
};
</script>

<style scoped>
/* Shorter than the fade it runs under, so the release never cuts the resize short. */
.picker-swap {
    transition: height 0.12s ease;
}

/* Hides content taller than the pinned height. The selection ring paints outside the
   palette's box, so the cut sits just past it rather than at the box edge. */
.picker-swap--resizing {
    clip-path: inset(-6px -8px);
}
</style>
